import { useSyncExternalStore } from "react";

/**
 * How a removal (unenrollment request) ended, remembered for the rest of the
 * browser session so Settings can show the outcome banner from the design.
 *
 * The backend stops serving a removal the moment it ends (the GET answers
 * 404) and records neither who ended it nor whether it was cancelled by the
 * initiator or rejected by another Owner. So:
 *
 *  - the Owner who acts records the outcome here before the GET catches up
 *    (`cancelled` when they started the request, `rejected` otherwise);
 *  - everyone else infers it when a pause they had seen disappears: past
 *    its deadline it `expired`, otherwise it `ended` by someone's hand.
 *
 * `ended` is the honest fallback until the backend exposes the outcome; see
 * the backend asks in the unenrollment thread.
 */
export type UnenrollmentOutcomeKind = "cancelled" | "rejected" | "expired" | "ended";

export type UnenrollmentOutcome = {
  kind: UnenrollmentOutcomeKind;
  txId: string;
  /** Who cancelled or rejected, when known. */
  actor: { userId: string; name: string } | null;
  /** The viewer is the one who acted. */
  byMe: boolean;
  dismissed: boolean;
  recordedAt: string;
};

/** The last pause this browser saw, so a vanished one can be explained. */
export type RememberedPause = {
  txId: string;
  expiresAt: string | null;
};

const OUTCOME_KEY = "sdp.unenrollment.outcome";
const PAUSE_KEY = "sdp.unenrollment.lastPause";

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

const read = <T>(key: string): T | null => {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

// `useSyncExternalStore` needs a stable snapshot between writes, so the
// outcome is cached in memory and only re-read from storage after a write
// (or on first access, e.g. after a reload).
let outcomeCache: { value: UnenrollmentOutcome | null } | null = null;

const write = (key: string, value: unknown | null) => {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable (private mode); the banner is a nicety.
  }
  if (key === OUTCOME_KEY) outcomeCache = null;
  notify();
};

export const getUnenrollmentOutcome = (): UnenrollmentOutcome | null => {
  if (!outcomeCache) outcomeCache = { value: read<UnenrollmentOutcome>(OUTCOME_KEY) };
  return outcomeCache.value;
};

export const recordUnenrollmentOutcome = (
  outcome: Omit<UnenrollmentOutcome, "dismissed" | "recordedAt">,
) => {
  const current = getUnenrollmentOutcome();
  // A specific outcome (who did what) always beats the inferred `ended`;
  // nothing else overwrites an outcome already recorded for the same removal.
  if (current && current.txId === outcome.txId) {
    if (current.kind !== "ended" || outcome.kind === "ended") return;
  }
  write(OUTCOME_KEY, {
    ...outcome,
    dismissed: false,
    recordedAt: new Date().toISOString(),
  } satisfies UnenrollmentOutcome);
};

export const dismissUnenrollmentOutcome = () => {
  const current = getUnenrollmentOutcome();
  if (!current || current.dismissed) return;
  write(OUTCOME_KEY, { ...current, dismissed: true });
};

export const clearUnenrollmentOutcome = () => {
  write(OUTCOME_KEY, null);
  write(PAUSE_KEY, null);
};

export const rememberPause = (pause: RememberedPause) => {
  const current = read<RememberedPause>(PAUSE_KEY);
  if (current?.txId === pause.txId && current.expiresAt === pause.expiresAt) return;
  write(PAUSE_KEY, pause);
};

export const takeRememberedPause = (): RememberedPause | null => {
  const current = read<RememberedPause>(PAUSE_KEY);
  if (current) write(PAUSE_KEY, null);
  return current;
};

/**
 * Explain a pause that is no longer served: expired if its deadline passed,
 * otherwise ended by someone (the backend does not say who).
 */
export const inferVanishedOutcome = (
  pause: RememberedPause,
  now: Date = new Date(),
): UnenrollmentOutcomeKind => {
  if (!pause.expiresAt) return "ended";
  const expiresAt = Date.parse(pause.expiresAt);
  return !Number.isNaN(expiresAt) && expiresAt <= now.getTime() ? "expired" : "ended";
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The recorded outcome, re-rendering when it is recorded or dismissed. */
export const useUnenrollmentOutcome = (): UnenrollmentOutcome | null =>
  useSyncExternalStore(subscribe, getUnenrollmentOutcome, () => null);
